/**
 * Idempotency support.
 *
 * The Gateway (Task 05) enforces idempotency platform-wide and the Reservation
 * Engine (Task 12) layers its own semantics on top. Both need the same thing:
 * a stable fingerprint of a request, so that replaying a key with a *different*
 * body is detected as a conflict rather than silently returning someone else's
 * response.
 *
 * The hashing uses `node:crypto` on the server side. The import is deferred so
 * that bundlers targeting the browser never evaluate it at module load time —
 * these functions are only called from server code, but the package is imported
 * by isomorphic modules.
 */
const HASH_ALGORITHM = 'sha256';
/**
 * Canonical JSON: object keys sorted recursively.
 *
 * `{a:1,b:2}` and `{b:2,a:1}` are the same request and must fingerprint
 * identically. `JSON.stringify` preserves insertion order, so it cannot be
 * used directly.
 */
export function canonicalJson(value) {
    // `JSON.stringify(undefined)` returns undefined at runtime even though its
    // signature promises string, so undefined is handled before the call rather
    // than patched afterwards.
    if (value === undefined)
        return 'null';
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value);
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    const entries = Object.entries(value)
        .filter(([, entryValue]) => entryValue !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entryValue]) => `${JSON.stringify(key)}:${canonicalJson(entryValue)}`);
    return `{${entries.join(',')}}`;
}
import * as nodeCrypto from 'node:crypto';
/**
 * Lazily imports `node:crypto`. Only called from server code, but the namespace
 * import itself never dereferences an export so bundlers targeting the browser
 * can externalize the module without crashing at module load time.
 */
function getNodeCrypto() {
    return nodeCrypto;
}
/**
 * Fingerprints a request for idempotency comparison.
 *
 * Method and path are included so the same key cannot be reused across
 * different operations.
 */
export function requestFingerprint(method, path, body) {
    const { createHash } = getNodeCrypto();
    return createHash(HASH_ALGORITHM)
        .update(`${method.toUpperCase()}\n${path}\n${canonicalJson(body)}`)
        .digest('hex');
}
/**
 * Constant-time fingerprint comparison.
 *
 * Idempotency keys are client-supplied and a mismatch is a security-relevant
 * decision, so this avoids leaking prefix length through timing.
 */
export function fingerprintsMatch(left, right) {
    const { timingSafeEqual } = getNodeCrypto();
    const leftBuffer = Buffer.from(left, 'utf8');
    const rightBuffer = Buffer.from(right, 'utf8');
    if (leftBuffer.length !== rightBuffer.length)
        return false;
    return timingSafeEqual(leftBuffer, rightBuffer);
}
/** Hashes a token for storage. Raw session and reset tokens are never persisted. */
export function hashToken(token) {
    const { createHash } = getNodeCrypto();
    return createHash(HASH_ALGORITHM).update(token).digest('hex');
}
//# sourceMappingURL=idempotency.js.map